import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface LeaderboardPageProps {
  onNavigate: (page: string, data?: any) => void;
}

export default function LeaderboardPage({ onNavigate }: LeaderboardPageProps) {
  const [tab, setTab] = useState<'weekly' | 'alltime' | 'debut'>('weekly');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetcher = tab === 'weekly' ? api.leaderboard.weeklyFollowers()
      : tab === 'alltime' ? api.leaderboard.allTime()
      : api.leaderboard.debut();

    fetcher
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tab]);

  const renderItem = (item: any, index: number) => {
    const story = item.story || item;
    return (
      <button
        key={story?.id || index}
        onClick={() => story?.id && onNavigate('story', { id: story.id, slug: story.slug })}
        className="w-full text-left flex items-center gap-4 p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-ink-500/50 transition-colors"
      >
        <span className={`text-2xl font-bold w-8 text-center ${
          index === 0 ? 'text-amber-400' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-700' : 'text-gray-600'
        }`}>
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{story?.title || 'Unknown'}</h3>
          <div className="flex gap-3 text-sm text-gray-400">
            <span>by {story?.penName?.penName || story?.author?.username || 'Unknown'}</span>
            {story?._count && (
              <>
                <span>{story._count.chapters} ch</span>
                <span>{story._count.follows} follows</span>
              </>
            )}
          </div>
        </div>
        {item.newFollowers && (
          <span className="px-3 py-1 bg-emerald-900/30 text-emerald-400 rounded-full text-sm font-medium">
            +{item.newFollowers} this week
          </span>
        )}
        {story?.totalViews !== undefined && (
          <span className="text-sm text-gray-500">{story.totalViews.toLocaleString()} views</span>
        )}
      </button>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Leaderboard</h1>

      <div className="flex gap-2 mb-6">
        {([
          { key: 'weekly', label: 'Weekly Top' },
          { key: 'alltime', label: 'All-Time' },
          { key: 'debut', label: 'Debut Stories' },
        ] as const).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-ink-600 text-white' : 'bg-gray-900 text-gray-400 border border-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data.length > 0 ? (
        <div className="space-y-3">
          {data.map((item, i) => renderItem(item, i))}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="inline-flex flex-col items-center gap-4 max-w-md">
            <div className="w-20 h-20 rounded-full bg-amber-900/20 border border-amber-700/30 flex items-center justify-center text-4xl">
              🏆
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">
                {tab === 'weekly' ? 'No stories ranked this week yet' :
                 tab === 'debut' ? 'No debut stories yet' :
                 'The all-time rankings are empty'}
              </h2>
              <p className="text-gray-400 mb-6">
                {tab === 'weekly'
                  ? 'The weekly leaderboard resets every Monday. Publish a story and gain followers to climb the ranks!'
                  : tab === 'debut'
                  ? 'New to ScribeHaven? Publish your first story and claim a debut spot — early authors have the best chance to rank!'
                  : 'Be among the first authors to build a readership on ScribeHaven and claim a permanent spot at the top.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => onNavigate('dashboard')}
                className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
              >
                Start Writing
              </button>
              <button
                onClick={() => onNavigate('discover')}
                className="px-6 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl font-medium transition-colors text-gray-300"
              >
                Discover Stories
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Rankings update daily based on followers, views, and reader engagement.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
